import React, { useCallback, useEffect, useMemo, useState } from "react";
import AtriumApi, { getApiErrorMessage } from "api";
import notify from "lib/notify";
import { statusTone } from "lib/statusTone";
import { MdAttachMoney, MdFileDownload, MdRefresh, MdTrendingUp } from "react-icons/md";
import { PageContainer, PageHeader } from "components/page";
import Card from "components/card";
import Button from "components/button";
import Badge from "components/badge";
import DataTable from "components/data-table";
import { SearchInput, Select } from "components/fields";
import { EmptyState, ErrorState } from "components/states";
import StatCard from "components/stat-card";
import { formatCurrency, formatDate, formatNumber } from "utils/format";

const PAGE_SIZE_OPTIONS = [10, 25, 50];

const TYPE_OPTIONS = [
  { value: "all", label: "All Donations" },
  { value: "one_time", label: "One-Time Only" },
  { value: "recurring", label: "Recurring Only" },
];

const DONATION_STATUS_TONES = { succeeded: "success", failed: "danger", refunded: "neutral" };

const FREQUENCY_LABELS = { month: "Monthly", quarter: "Quarterly", year: "Yearly" };

const CSV_HEADERS = ["Donor Name", "Email", "Amount", "Type", "Status", "Payment Intent ID", "Date"];

// Donation and stat amounts are stored in cents.
const formatCents = (cents) => formatCurrency(cents / 100);

const donationStatus = (donation) => donation.status || "completed";

const donationStatusTone = (status) => DONATION_STATUS_TONES[status] || statusTone(status);

const toLabel = (value) =>
  value
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const matchesFilters = (donation, filterType, searchQuery) => {
  if (filterType === "one_time" && donation.isRecurring) return false;
  if (filterType === "recurring" && !donation.isRecurring) return false;

  const query = searchQuery.trim().toLowerCase();
  if (!query) return true;
  return Boolean(
    donation.donorName?.toLowerCase().includes(query) ||
      donation.donorEmail?.toLowerCase().includes(query) ||
      donation.stripePaymentIntentId?.toLowerCase().includes(query)
  );
};

// Requests every donation in one page; retries with the larger total if more arrived meanwhile.
const fetchAllDonations = async (knownTotal) => {
  const response = await AtriumApi.get(`/connect/donations?limit=${knownTotal}&offset=0`);
  const rows = response.data.data || [];
  const total = response.data.total ?? rows.length;
  return total > rows.length ? fetchAllDonations(total) : rows;
};

const downloadCsv = (donations) => {
  const rows = donations.map((donation) => [
    donation.donorName || "Anonymous",
    donation.donorEmail || "",
    `$${(donation.amount / 100).toFixed(2)}`,
    donation.isRecurring ? "Recurring" : "One-Time",
    donationStatus(donation),
    donation.stripePaymentIntentId || "",
    formatDate(donation.createdAt),
  ]);

  const csvContent = [
    CSV_HEADERS.join(","),
    ...rows.map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(",")),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", `donations-${Date.now()}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const donorCell = ({ row }) => (
  <div className="min-w-0">
    <p className="truncate font-medium text-fg">{row.original.donorName || "Anonymous"}</p>
    {row.original.donorEmail && (
      <p className="truncate text-xs text-fg-muted">{row.original.donorEmail}</p>
    )}
  </div>
);

const amountColumn = {
  id: "amount",
  header: "Amount",
  meta: { align: "right" },
  cell: ({ row }) => <span className="font-semibold">{formatCents(row.original.amount)}</span>,
};

const DonationsAnalytics = () => {
  const [stats, setStats] = useState(null);
  const [statsError, setStatsError] = useState(null);
  const [statsReload, setStatsReload] = useState(0);

  const [recurring, setRecurring] = useState({ rows: [], total: null });
  const [recurringLoading, setRecurringLoading] = useState(true);
  const [recurringError, setRecurringError] = useState(null);
  const [recurringPageIndex, setRecurringPageIndex] = useState(0);
  const [recurringPageSize, setRecurringPageSize] = useState(10);
  const [recurringReload, setRecurringReload] = useState(0);

  const [history, setHistory] = useState({ rows: [], total: null });
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [historyReload, setHistoryReload] = useState(0);

  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [allDonations, setAllDonations] = useState(null);
  const [allLoading, setAllLoading] = useState(false);
  const [allError, setAllError] = useState(null);

  const [exporting, setExporting] = useState(false);

  const filtersActive = filterType !== "all" || searchQuery.trim() !== "";
  const donationTotal = history.total;

  useEffect(() => {
    let cancelled = false;
    setStatsError(null);
    AtriumApi.get("/connect/donations/stats")
      .then((response) => {
        if (!cancelled && response.data.success) setStats(response.data.data || {});
      })
      .catch((error) => {
        if (!cancelled) setStatsError(error);
      });
    return () => {
      cancelled = true;
    };
  }, [statsReload]);

  useEffect(() => {
    let cancelled = false;
    setRecurringLoading(true);
    setRecurringError(null);
    AtriumApi.get(
      `/connect/recurring-donations?status=active&limit=${recurringPageSize}&offset=${
        recurringPageIndex * recurringPageSize
      }`
    )
      .then((response) => {
        if (!cancelled && response.data.success) {
          setRecurring({ rows: response.data.data || [], total: response.data.total ?? 0 });
        }
      })
      .catch((error) => {
        if (!cancelled) setRecurringError(error);
      })
      .finally(() => {
        if (!cancelled) setRecurringLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [recurringPageIndex, recurringPageSize, recurringReload]);

  useEffect(() => {
    if (filtersActive) return undefined;
    let cancelled = false;
    setHistoryLoading(true);
    setHistoryError(null);
    AtriumApi.get(`/connect/donations?limit=${pageSize}&offset=${pageIndex * pageSize}`)
      .then((response) => {
        if (!cancelled && response.data.success) {
          setHistory({ rows: response.data.data || [], total: response.data.total ?? 0 });
        }
      })
      .catch((error) => {
        if (!cancelled) setHistoryError(error);
      })
      .finally(() => {
        if (!cancelled) setHistoryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filtersActive, pageIndex, pageSize, historyReload]);

  // Search and type filters run client-side, so they need every donation loaded.
  useEffect(() => {
    if (!filtersActive || allDonations || allError || !donationTotal) return undefined;
    let cancelled = false;
    setAllLoading(true);
    fetchAllDonations(donationTotal)
      .then((rows) => {
        if (!cancelled) setAllDonations(rows);
      })
      .catch((error) => {
        if (!cancelled) setAllError(error);
      })
      .finally(() => {
        if (!cancelled) setAllLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filtersActive, allDonations, allError, donationTotal]);

  const reloadAllDonations = () => {
    setAllDonations(null);
    setAllError(null);
  };

  const handleRefresh = () => {
    setStatsReload((n) => n + 1);
    setRecurringReload((n) => n + 1);
    setHistoryReload((n) => n + 1);
    reloadAllDonations();
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setPageIndex(0);
  };

  const handleFilterTypeChange = (event) => {
    setFilterType(event.target.value);
    setPageIndex(0);
  };

  const clearFilters = () => {
    setSearchQuery("");
    setFilterType("all");
    setPageIndex(0);
  };

  const handlePageSizeChange = useCallback((size) => {
    setPageSize(size);
    setPageIndex(0);
  }, []);

  const handleRecurringPageSizeChange = useCallback((size) => {
    setRecurringPageSize(size);
    setRecurringPageIndex(0);
  }, []);

  const handleExport = async () => {
    setExporting(true);
    try {
      const donations = (await fetchAllDonations(donationTotal)).filter((donation) =>
        matchesFilters(donation, filterType, searchQuery)
      );
      if (donations.length === 0) {
        notify.error("No data to export");
        return;
      }
      downloadCsv(donations);
      notify.success("CSV exported");
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not export donations"));
    } finally {
      setExporting(false);
    }
  };

  const filteredDonations = useMemo(
    () =>
      filtersActive && allDonations
        ? allDonations.filter((donation) => matchesFilters(donation, filterType, searchQuery))
        : [],
    [filtersActive, allDonations, filterType, searchQuery]
  );

  const recurringColumns = useMemo(
    () => [
      { id: "donor", header: "Donor", cell: donorCell },
      {
        id: "frequency",
        header: "Frequency",
        cell: ({ row }) =>
          FREQUENCY_LABELS[row.original.frequency] || row.original.frequency,
      },
      amountColumn,
      {
        id: "started",
        header: "Started",
        cell: ({ row }) => formatDate(row.original.createdAt),
      },
      {
        id: "nextBilling",
        header: "Next Billing",
        cell: ({ row }) => formatDate(row.original.nextBillingDate),
      },
    ],
    []
  );

  const historyColumns = useMemo(
    () => [
      { id: "donor", header: "Donor", cell: donorCell },
      amountColumn,
      {
        id: "type",
        header: "Type",
        cell: ({ row }) =>
          row.original.isRecurring ? (
            <Badge tone="brand">Recurring</Badge>
          ) : (
            <Badge tone="neutral">One-Time</Badge>
          ),
      },
      {
        id: "status",
        header: "Status",
        cell: ({ row }) => {
          const status = donationStatus(row.original);
          return <Badge tone={donationStatusTone(status)}>{toLabel(status)}</Badge>;
        },
      },
      {
        id: "date",
        header: "Date",
        cell: ({ row }) => (
          <span className="text-fg-muted">{formatDate(row.original.createdAt)}</span>
        ),
      },
    ],
    []
  );

  const renderStats = () => {
    if (statsError) {
      return (
        <Card padding="md">
          <ErrorState
            message={getApiErrorMessage(statsError, "Could not load donation statistics")}
            onRetry={() => setStatsReload((n) => n + 1)}
          />
        </Card>
      );
    }
    const statsLoading = stats === null;
    return (
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          icon={MdAttachMoney}
          label="Total Raised"
          value={formatCents(stats?.grandTotal || 0)}
          loading={statsLoading}
        />
        <StatCard
          icon={MdTrendingUp}
          accent="sky"
          label="Total Donations"
          value={formatNumber(stats?.totalDonations || 0)}
          loading={statsLoading}
        />
        <StatCard
          icon={MdAttachMoney}
          accent="gold"
          label="One-Time Donations"
          value={formatCents(stats?.oneTimeTotal || 0)}
          loading={statsLoading}
        />
        <StatCard
          icon={MdRefresh}
          accent="slate"
          label="Recurring Revenue"
          value={formatCents(stats?.recurringTotal || 0)}
          loading={statsLoading}
        />
      </div>
    );
  };

  const renderRecurring = () => {
    if (recurringError) {
      return (
        <section className="flex flex-col gap-3">
          <h2 className="text-heading">Active Recurring Donations</h2>
          <Card padding="md">
            <ErrorState
              message={getApiErrorMessage(recurringError, "Could not load recurring donations")}
              onRetry={() => setRecurringReload((n) => n + 1)}
            />
          </Card>
        </section>
      );
    }
    if (!recurring.total) return null;
    return (
      <section className="flex flex-col gap-3">
        <h2 className="text-heading">
          Active Recurring Donations ({formatNumber(recurring.total)})
        </h2>
        <DataTable
          aria-label="Active recurring donations"
          columns={recurringColumns}
          data={recurring.rows}
          loading={recurringLoading}
          getRowId={(donation) => String(donation.id)}
          pagination={{
            pageIndex: recurringPageIndex,
            pageSize: recurringPageSize,
            total: recurring.total,
            onPageChange: setRecurringPageIndex,
            onPageSizeChange: handleRecurringPageSizeChange,
            pageSizeOptions: PAGE_SIZE_OPTIONS,
          }}
        />
      </section>
    );
  };

  const renderHistoryBody = () => {
    if (historyError && !filtersActive) {
      return (
        <Card padding="md">
          <ErrorState
            message={getApiErrorMessage(historyError, "Could not load donations")}
            onRetry={() => setHistoryReload((n) => n + 1)}
          />
        </Card>
      );
    }
    if (allError && filtersActive) {
      return (
        <Card padding="md">
          <ErrorState
            message={getApiErrorMessage(allError, "Could not load donations")}
            onRetry={reloadAllDonations}
          />
        </Card>
      );
    }
    if (donationTotal === 0) {
      return (
        <Card padding="md">
          <EmptyState
            icon={MdAttachMoney}
            title="No Donations Yet"
            description="Donations will appear here when received."
          />
        </Card>
      );
    }

    const rows = filtersActive
      ? filteredDonations.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize)
      : history.rows;
    const total = filtersActive ? filteredDonations.length : donationTotal ?? 0;
    const loading = filtersActive ? allLoading || !allDonations : historyLoading;

    return (
      <DataTable
        aria-label="Donation history"
        columns={historyColumns}
        data={rows}
        loading={loading}
        getRowId={(donation) => String(donation.id)}
        emptyState={
          filtersActive ? (
            <EmptyState
              variant="filtered"
              title="No donations match your filters"
              action={
                <Button variant="ghost" onClick={clearFilters}>
                  Clear Filters
                </Button>
              }
            />
          ) : undefined
        }
        pagination={{
          pageIndex,
          pageSize,
          total,
          onPageChange: setPageIndex,
          onPageSizeChange: handlePageSizeChange,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
        }}
      />
    );
  };

  const showFilters = Boolean(donationTotal) && !(historyError && !filtersActive);

  return (
    <PageContainer>
      <PageHeader
        title="Donations"
        description="View donation history and statistics"
        actions={
          <>
            <Button variant="secondary" leftIcon={<MdRefresh />} onClick={handleRefresh}>
              Refresh
            </Button>
            {donationTotal > 0 && (
              <Button leftIcon={<MdFileDownload />} onClick={handleExport} loading={exporting}>
                Export CSV
              </Button>
            )}
          </>
        }
      />

      {renderStats()}

      {renderRecurring()}

      <section className="flex flex-col gap-3">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <h2 className="text-heading">Donation History</h2>
          {showFilters && (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              {filtersActive && allDonations && (
                <p className="text-sm text-fg-muted">
                  {formatNumber(filteredDonations.length)} of {formatNumber(donationTotal)}
                </p>
              )}
              <SearchInput
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search by donor name, email, or transaction ID…"
                ariaLabel="Search donations"
                className="w-full sm:w-80"
              />
              <Select
                aria-label="Donation type"
                value={filterType}
                onChange={handleFilterTypeChange}
                options={TYPE_OPTIONS}
                className="w-full sm:w-44"
              />
            </div>
          )}
        </div>
        {renderHistoryBody()}
      </section>
    </PageContainer>
  );
};

export default DonationsAnalytics;
